/**
 * NeedCardModel — the ONE card contract every needs-you surface renders.
 *
 * Law (founder, 07-15): the card IS the gate. Truth is minted once, at the
 * source, in full; home, notch, and the task stream render the SAME object;
 * no downstream paraphrasing, ever. If you are building a needs-you card any
 * other way, you are re-introducing the "Write note message" desync class
 * this file exists to kill.
 *
 * Intents are DATA, not closures, so a derived card can be persisted at mint
 * time (PR-N3, interactions.card) and rendered by surfaces that never see
 * the request union (the notch).
 *
 * Spec: bash-notes/needs-you-truth-spec-2026-07-15.md
 */

import type { AutopilotSuggestion } from './autopilot.js';
import type { InteractionOutcome, InteractionRequest } from './interaction.js';
import type { Question } from './messages.js';
import { needKindForInteraction, type NeedKind } from './need-kind.js';
import { planUsedUpLine } from '../engines/standing.js';
import type { NamiProposal } from './nami-proposals.js';
import { PURCHASE_NEED_KICKER, PURCHASE_NEED_TITLE, PURCHASE_NEED_WHY } from '../wallet/copy.js';

export type NeedCardIntent =
  | { type: 'resolve'; outcome: InteractionOutcome }
  | { type: 'oauth'; toolkit: string }
  | { type: 'answer' }
  | { type: 'open_task' }
  | { type: 'accept_proposal' }
  | { type: 'decline_proposal' }
  | { type: 'accept_suggestion' }
  | { type: 'dismiss_suggestion' };

export interface NeedCardAction {
  id: string;
  label: string;
  /** Exactly one primary per card — the filled-ink button. */
  primary?: boolean;
  intent: NeedCardIntent;
}

export interface NeedCardModel {
  /** Render key, unique across sources: `interaction-<id>` / `proposal-<id>` / `autopilot-<id>`. */
  id: string;
  source: 'interaction' | 'proposal' | 'autopilot';
  interactionId?: string;
  proposalId?: string;
  suggestionId?: string;
  taskId?: string;
  /** Underlying task's title (interaction cards) — the delete ask names the
   *  TASK, never the card's imperative headline. */
  taskTitle?: string | null | undefined;
  /**
   * R-A: the door the underlying task was born through, so an ask raised by
   * channel-born work can say where it came from ("Question · via WhatsApp")
   * and the reader knows they can answer in the chat instead. Interaction
   * cards only; proposals and autopilot suggestions have no task behind them.
   */
  originChannel?: string | null | undefined;
  kind: NeedKind;
  /**
   * Overrides the NEED_KIND_LABELS word in the card's kind slot. Minted only
   * where the kind word alone under-informs (proposals name their artifact:
   * "Proposed task" / "Proposed routine" / "Proposed skill").
   */
  eyebrow?: string;
  title: string;
  why: string;
  /**
   * Done rail (08-22): this ask was minted by the task-end verdict
   * (request.source === 'done_verdict'), whose row deliberately never rides
   * the continue rail (no bound tool_use). An inline answer must therefore be
   * DELIVERED AS A NORMAL TASK MESSAGE (the daily send rail) and the row
   * resolved as acknowledged — resolving with answers alone would swallow them.
   */
  answerAsMessage?: boolean;
  /**
   * Quiet mono provenance line under the why. Only proposals carry one —
   * they are the one card born from observing the user work, so they are
   * the one card that shows where they came from.
   */
  receipt?: string;
  /** Toolkit slug for the brand mark; absent = no mark, never a generic glyph. */
  appSlug?: string;
  actions: NeedCardAction[];
  /** ask_question only — powers the inline answer panel. */
  questions?: Question[];
  createdAt: string;
}

export interface NeedCardContext {
  interactionId: string;
  taskId: string;
  taskTitle?: string | null;
  /** R-A: the task's birth channel, joined off the interaction's task row. */
  originChannel?: string | null;
  /** ISO timestamp from the interactions row. */
  createdAt: string;
}

const DECLINED: InteractionOutcome = { behavior: 'deny', reason: 'user_declined' };

/** "$12" for whole dollars, "$0.50" otherwise. */
export function formatUsd(amount: number): string {
  return Number.isInteger(amount) ? `$${amount}` : `$${amount.toFixed(2)}`;
}

/** First ~max chars on a word boundary, single-line, ellipsis when cut. */
export function excerpt(text: string, max = 160): string {
  const oneLine = text.replace(/\s+/g, ' ').trim();
  if (oneLine.length <= max) return oneLine;
  const cut = oneLine.slice(0, max);
  return `${cut.slice(0, Math.max(cut.lastIndexOf(' '), 40))}…`;
}

/**
 * The headline of a permission ask: the sandbox's plain-language summary, else a
 * neutral question. Never the tool's name. "Allow Bash?" read as "allow the
 * agent?" (Bash is our agent's name AND the shell tool's), and an mcp__ name is
 * protocol noise to a non-technical user (#bugs 2026-09-25, Adarsh: "I can't
 * understand what I'm allowing"). One mint for every surface: the ask card, the
 * home and Island need card, the ask notification, the extension.
 */
export function permissionHeadline(summary: string | undefined | null): string {
  return (typeof summary === 'string' && summary.trim()) || 'Allow this step?';
}

/**
 * Project a durable interaction request into the card every surface renders.
 * Exhaustive over the union ON PURPOSE (no default): a new InteractionKind
 * must fail compilation here until someone defines its card.
 */
export function deriveNeedCard(request: InteractionRequest, ctx: NeedCardContext): NeedCardModel {
  const base = {
    id: `interaction-${ctx.interactionId}`,
    source: 'interaction' as const,
    interactionId: ctx.interactionId,
    taskId: ctx.taskId,
    taskTitle: ctx.taskTitle,
    originChannel: ctx.originChannel,
    kind: needKindForInteraction(request.kind, request as { intent?: string; renderer?: string }),
    createdAt: ctx.createdAt,
  };
  const inTask = ctx.taskTitle ? ` while working on “${ctx.taskTitle}”` : '';

  switch (request.kind) {
    case 'ask_question': {
      const first = request.questions[0];
      const more = request.questions.length - 1;
      return {
        ...base,
        ...(request.source === 'done_verdict' ? { answerAsMessage: true } : {}),
        title: first?.question?.trim() || ctx.taskTitle || 'A question for you',
        why: more > 0
          ? `And ${more} more question${more > 1 ? 's' : ''} waiting with it.`
          : `Asked${inTask}. Answer here and the task carries on.`,
        actions: [{ id: 'answer', label: 'Answer', primary: true, intent: { type: 'answer' } }],
        questions: request.questions,
      };
    }
    case 'composio_auth':
      return {
        ...base,
        // Verbatim the in-stream AuthCard line (AuthCard.tsx). The two
        // surfaces must never disagree; change both together or neither.
        title: `Connect ${request.displayName} to continue`,
        why: ctx.taskTitle
          ? `“${ctx.taskTitle}” is paused until ${request.displayName} is connected.`
          : `The task is paused until ${request.displayName} is connected.`,
        appSlug: request.toolkit,
        actions: [
          { id: 'connect', label: `Connect ${request.displayName}`, primary: true, intent: { type: 'oauth', toolkit: request.toolkit } },
          { id: 'skip', label: 'Skip', intent: { type: 'resolve', outcome: DECLINED } },
        ],
      };
    case 'tool_permission':
      return {
        ...base,
        title: permissionHeadline(request.summary),
        why: `Asked${inTask}. Allowing runs this call only.`,
        ...(request.appSlug ? { appSlug: request.appSlug } : {}),
        actions: [
          { id: 'allow', label: 'Allow', primary: true, intent: { type: 'resolve', outcome: { behavior: 'allow' } } },
          { id: 'deny', label: 'Not now', intent: { type: 'resolve', outcome: DECLINED } },
        ],
      };
    case 'email_send_approval':
      // Agent email: the server wrote these words from the email it stored; the yes sends exactly that email.
      return {
        ...base,
        title: permissionHeadline(request.summary),
        why: request.reason.line,
        actions: [
          { id: 'allow', label: request.actionLabel || 'Send email', primary: true, intent: { type: 'resolve', outcome: { behavior: 'allow' } } },
          { id: 'deny', label: 'Not now', intent: { type: 'resolve', outcome: DECLINED } },
        ],
      };
    case 'credit_approval': {
      const cost = formatUsd(request.estimatedCostUsd);
      return {
        ...base,
        title: `Approve ${cost} for ${request.toolName}`,
        why: `Your balance is ${formatUsd(request.balanceUsd)}. The task continues the moment you approve.`,
        actions: [
          { id: 'approve', label: `Approve ${cost}`, primary: true, intent: { type: 'resolve', outcome: { behavior: 'allow' } } },
          { id: 'decline', label: 'Decline', intent: { type: 'resolve', outcome: DECLINED } },
        ],
      };
    }
    case 'exit_plan':
      return {
        ...base,
        title: ctx.taskTitle ? `Plan ready: ${ctx.taskTitle}` : 'A plan is ready for your sign-off',
        why: excerpt(request.plan),
        actions: [
          // Same wire as the in-task approve (ExitPlanInput → bare allow).
          { id: 'approve', label: 'Approve plan', primary: true, intent: { type: 'resolve', outcome: { behavior: 'allow' } } },
          { id: 'read', label: 'Read full plan', intent: { type: 'open_task' } },
        ],
      };
    case 'hands_approval':
      return {
        ...base,
        title: request.label?.trim()
          || (request.firstEngage ? 'Let the agent use your Mac' : 'An action on your screen needs a yes'),
        why: request.reasons?.length ? request.reasons.join(' ') : `Asked${inTask}.`,
        actions: [
          // Nonce echo is mandatory: the hands gate treats a nonce-less or
          // mismatched allow as declined.
          { id: 'approve', label: 'Approve', primary: true, intent: { type: 'resolve', outcome: { behavior: 'allow', nonce: request.nonce } } },
          { id: 'decline', label: 'Decline', intent: { type: 'resolve', outcome: DECLINED } },
        ],
      };
    case 'plan_limit':
      // Engines T5: a doorway, never a resolve. Continuing on Vestra needs the live balance and
      // another plan needs its live state, which only the card in the task has (and the times
      // it shows are the reader's local time, which a persisted card cannot know).
      return {
        ...base,
        title: planUsedUpLine(request.engine),
        why: 'Bash stopped at a safe point. Nothing is lost.',
        actions: [{ id: 'open', label: 'Open task', primary: true, intent: { type: 'open_task' } }],
      };
    case 'custom':
      // Agent wallet: a purchase card's title and description are the box's words (an amount, a shop), so no surface
      // away from the task repeats them (Home, the Island, the ask email and Slack line all read this card). A fixed
      // line, the task it belongs to, and the door to it; the amount and the shop are read from the server's purchase
      // inside the task and in Settings. The renderer is kept a literal (wallet/frames.ts PURCHASE_RENDERER), as
      // need-kind.ts keeps it, so this leaf imports no schema.
      if (request.renderer === 'purchase') {
        return {
          ...base,
          eyebrow: PURCHASE_NEED_KICKER,
          title: PURCHASE_NEED_TITLE,
          why: ctx.taskTitle?.trim() || PURCHASE_NEED_WHY,
          actions: [{ id: 'open', label: 'Open task', primary: true, intent: { type: 'open_task' } }],
        };
      }
      return {
        ...base,
        title: request.title?.trim() || ctx.taskTitle || 'A task needs you',
        why: request.description?.trim() || 'Open the task to respond.',
        // Custom kinds have bespoke in-stream renderers; the home card is an
        // honest doorway, not a re-implementation.
        actions: [{ id: 'open', label: 'Open task', primary: true, intent: { type: 'open_task' } }],
      };
  }
}

/**
 * Autopilot suggestions ride the same contract (PR-N2, needs-you-unified):
 * a spotted pattern is a Proposal-kind ask like Nami's — verdicts go to the
 * autopilot suggestion routes. The integration slug carries the brand mark.
 */
export function deriveAutopilotNeedCard(s: AutopilotSuggestion): NeedCardModel {
  return {
    id: `autopilot-${s.id}`,
    source: 'autopilot',
    suggestionId: s.id,
    kind: 'proposal',
    title: s.title?.trim() || 'A pattern worth automating',
    why: excerpt(s.description),
    appSlug: s.integration,
    actions: [
      { id: 'accept', label: 'Approve', primary: true, intent: { type: 'accept_suggestion' } },
      { id: 'dismiss', label: 'Dismiss', intent: { type: 'dismiss_suggestion' } },
    ],
    createdAt: s.createdAt,
  };
}

/** The eyebrow names the artifact a proposal would create (founder 07-22:
 *  "no information if a skill or a routine or a task"). */
const PROPOSAL_EYEBROWS: Record<NamiProposal['kind'], string> = {
  task: 'Proposed task',
  routine: 'Proposed routine',
  skill: 'Proposed skill',
  permission_rule: 'Proposed permission',
  prep_pack: 'Meeting prep',
};

/** The primary button names the creation, never a generic "Approve". */
const PROPOSAL_ACCEPT_LABELS: Record<NamiProposal['kind'], string> = {
  task: 'Create task',
  routine: 'Start routine',
  skill: 'Add skill',
  permission_rule: 'Stop asking',
  prep_pack: 'Open prep',
};

/** A permission proposal points two ways, and the card must say which (W4-4):
 *  granting a standing rule, or removing one that has saved the user nothing. */
const REVOKE_EYEBROW = 'Unused permission';
const REVOKE_ACCEPT_LABEL = 'Remove it';

/** "Jul 18" — deterministic short date for the provenance receipt. */
function shortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/** Nami proposals ride the same contract (verdicts go to proposal routes). */
export function deriveProposalNeedCard(p: NamiProposal): NeedCardModel {
  const payload = (typeof p.payload === 'object' && p.payload !== null ? p.payload : {}) as {
    name?: unknown;
    title?: unknown;
    action?: unknown;
  };
  // A permission proposal that REVOKES reads the opposite way from one that grants,
  // so the eyebrow and the button flip with the payload's direction (W4-4).
  const isRevoke = p.kind === 'permission_rule' && payload.action === 'revoke';
  // Task payloads carry `title`, skill/routine payloads carry `name` — read
  // both or task proposals render the fallback string (07-22 staging bug).
  const name = String(payload.title ?? payload.name ?? '').trim();
  const proposedOn = shortDate(p.created_at);
  return {
    id: `proposal-${p.id}`,
    source: 'proposal',
    proposalId: p.id,
    kind: 'proposal',
    eyebrow: (isRevoke ? REVOKE_EYEBROW : PROPOSAL_EYEBROWS[p.kind]) ?? 'Proposal',
    title: name || 'A suggestion from Bash',
    why: p.rationale,
    receipt: `From your activity${proposedOn ? ` · proposed ${proposedOn}` : ''}`,
    actions: [
      {
        id: 'accept',
        label: (isRevoke ? REVOKE_ACCEPT_LABEL : PROPOSAL_ACCEPT_LABELS[p.kind]) ?? 'Approve',
        primary: true,
        intent: { type: 'accept_proposal' },
      },
      { id: 'decline', label: 'Decline', intent: { type: 'decline_proposal' } },
    ],
    createdAt: p.created_at,
  };
}
